// src/components/ActiveMemberView.jsx
import React, { useReducer, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { DateTime } from 'luxon';

// --- State Reducer ---
const initialState = { loading: false, error: null, success: null };
const stateReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null, success: null };
    case 'SET_ERROR':
      return { ...state, loading: false, error: action.payload, success: null };
    case 'SET_SUCCESS':
      return { ...state, loading: false, error: null, success: action.payload };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
};

// --- Helper: Stripe Price Tiers (Excludes Free Trial) ---
const PAID_PRICE_TIERS = {
  'Starter Membership': 'Starter',
  'Professional': 'Professional',
  'Business Membership': 'Business'
};

// --- Key for Trial tier lookup ---
const TRIAL_TIER_KEY = 'TRIAL MEMBERSHIP';
const TRIAL_TIER_SLUG = 'trial membership';

const normalizeTier = (tier) => (typeof tier === 'string' ? tier.trim().toLowerCase() : '');
const isTrialTier = (tier) => {
  const normalized = normalizeTier(tier);
  return normalized === TRIAL_TIER_SLUG || normalized === 'trial';
};

// --- Helper: Credit Pack Sizes ---
const CREDIT_PACK_SIZES = [25, 50, 100, 250, 500, 1000];

// --- Main Component ---
function ActiveMemberView({ subscriptionProducts = [], creditPackProducts = [], currentMembership = {} }) {
  const { user, supabase, csrfToken, fetchWrapper, fetchCsrfToken, membership, initializeMembership } = useAuth();  // Added initializeMembership
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const navigate = useNavigate();
  const safeMembership = currentMembership || membership || {};
  const isTrial = isTrialTier(safeMembership.tier);
  const isCancelling = safeMembership.status === 'cancelling';
  const [isRefreshing, setIsRefreshing] = useState(false);  // ADDED: For spinner during refresh

  // Local products fetch (direct, for self-sufficiency)
  const [localProducts, setLocalProducts] = useState({ subscriptions: [], creditPackProducts: [] });
  const [isLoadingLocalProducts, setIsLoadingLocalProducts] = useState(true);
  console.log("[ActiveSubscriptionView] Rendering. Props received:" )

  // Fetch local products on mount (direct fetch)
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingLocalProducts(true);
      let currentCsrf = csrfToken;
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_tiers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': currentCsrf,
          },
          body: JSON.stringify({}),
          credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok || !Array.isArray(data)) throw new Error('Failed to fetch products');
        setLocalProducts({
          subscriptions: data.filter(p => p.is_membership === true),
          creditPackProducts: data.filter(p => p.is_membership === false)
        });
      } catch (error) {
        console.error('Products fetch error:', error);
      } finally {
        setIsLoadingLocalProducts(false);
      }
    };
    if (user.id) fetchProducts();
  }, [user.id, csrfToken, fetchCsrfToken]);

  // useEffect for success query handling/refresh/auto-route (direct fetch)
  // useEffect for success query handling/refresh/auto-route (direct fetch)
// ActiveMemberView.jsx useEffect for success query handling/refresh/auto-route (replace entire ~line 50)
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  if (success && success !== 'false') {
    const refreshMembership = async () => {
      let currentCsrf = csrfToken;
      try {
        if (!currentCsrf) currentCsrf = await fetchCsrfToken();
        const body = JSON.stringify({ p_user_id: user.id });
        console.log('Debug - body being sent:', body);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_membership2`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': currentCsrf,
          },
          body: body,
          credentials: 'include',
        });
        if (!response.ok) throw new Error('RPC failed');
        const data = await response.json();
        console.log('Refreshed membership post-success:', data);
        // Sync flags to AuthContext (await for settle)
        await initializeMembership(user.id);
        console.log('Initialize complete - routing to dashboard');  // Debug
        // Dynamic route (updated_type from query or default 'free')
        const updatedType = urlParams.get('updated_type') || 'free';
        navigate(`/estate-sales-pipeline/userdashboard?updated=true&updated_type=${updatedType}`, { replace: true });
      } catch (err) {
        console.error('Refresh error:', err);
        dispatch({ type: 'SET_ERROR', payload: `Refresh failed: ${err.message}` });
      }
    };
    if (user.id) refreshMembership();
  }
}, [user.id, csrfToken, fetchCsrfToken, navigate, initializeMembership]);
  // Stripe Click Handlers
  const handleStripePurchase = async (priceId, mode = 'subscription') => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId: user.id,
          priceId: priceId,
          mode: mode
        }),
        credentials: 'include',
      });
      const session = await response.json();
      if (!response.ok || session.error) {
        throw new Error(session.error || `Failed to create checkout session (Status: ${response.status})`);
      }
      window.location.href = session.url;
    } catch (err) {
      console.error('Stripe purchase error:', err);
      dispatch({ type: 'SET_ERROR', payload: `Stripe error: ${err.message}` });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };
  // RETROFIT: Simplified handler for subscription switches (only active -> different tier needs confirm + cancel)
const handleSelectSubscriptionTier = async (tier) => {
  console.log(`${new Date().toISOString()} - handleSelectSubscriptionTier triggered for tier:`, tier.tier);
  const isCurrent = tier.tier === safeMembership.tier;
  const isActive = safeMembership.status === 'active';

  // Early return if current and active (already disabled in render)
  if (isCurrent && isActive) {
    console.log(`${new Date().toISOString()} - Skipping: current active tier`);
    return;
  }

  // Only add confirm + cancel for active switches (different tier)
  if (isActive && !isCurrent) {
    const confirmMessage = `This will switch your membership to ${tier.tier}. Current Upload Credits will roll over into the new membership. Continue?`;
    if (!window.confirm(confirmMessage)) {
      console.log(`${new Date().toISOString()} - User cancelled confirm`);
      return;
    }

    // Cancel active sub before switch
    console.log(`${new Date().toISOString()} - Cancelling active sub before switch`);
    await handleCancelMembership({ skipConfirm: true }); // Schedules end; UI refreshes via event + initialize

    // Optional UX delay (from original, reduces perceived race)
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  // Proceed to purchase (works for active switches post-cancel, or direct for cancelling/trial)
  console.log(`${new Date().toISOString()} - Fetching Stripe checkout for:`, tier.tier);
  await handleStripePurchase(tier.stripe_price_id, 'subscription');
};
const handleCancelMembership = async ({ skipConfirm = false } = {}) => {
  // CHANGE 1: Early auth check (prevents null user.id calls to RPC/endpoint)
  if (!user.id) {
    console.error('Cancel membership error: User not authenticated (missing id)');
    dispatch({ type: 'SET_ERROR', payload: 'Error: User not authenticated. Please sign in again.' });
    return;
  }

  if (!skipConfirm && !window.confirm("Are you sure you want to cancel your membership? This will take effect at the end of your current billing period.")) {
    return;
  }
  dispatch({ type: 'SET_LOADING', payload: true });
  try {
    let subscriptionId = safeMembership.stripe_subscription_id;
    // CHANGE 2: Debug log for user.id (remove post-test)
    console.log('Cancel membership: user.id =', user.id, 'subscriptionId from state =', subscriptionId);
    // NEW: Fallback refresh if null (pull full row from RPC/DB, handle raw array/envelope)
    if (!subscriptionId) {
      console.log('No subscription ID in state - querying DB...');
      const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_membership2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_user_id: user.id }),
      });
      const rpcResponse = await response.json();
      console.log('Raw RPC response:', rpcResponse); // Debug (remove post-test)
      const row = rpcResponse[0] || rpcResponse.data?.[0]; // FIXED: Raw array first, then envelope
      subscriptionId = row?.stripe_subscription_id;
      if (!subscriptionId) {
        throw new Error('No active Stripe subscription ID found in database.');
      }
    }
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/stripe/cancel-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: user.id,
        subscriptionId: subscriptionId
      }),
      credentials: 'include',
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `Cancellation request failed (Status: ${response.status})`);
    }
    dispatch({ type: 'SET_SUCCESS', payload: `Membership switch initiated. Current plan ends on ${DateTime.fromISO(result.cancel_at).toLocaleString(DateTime.DATE_MED)}. Redirecting to new plan...` });
    window.dispatchEvent(new Event('membershipUpdated')); // Triggers AuthContext refresh
    await initializeMembership(user.id); // Re-run check_membership2 to sync flags (status='cancelling')
  } catch (err) {
    console.error('Cancel membership error:', err);
    dispatch({ type: 'SET_ERROR', payload: `Error canceling membership: ${err.message}` });
  } finally {
    dispatch({ type: 'SET_LOADING', payload: false });  // CHANGE 3: Always clear loading (in finally)
  }
};

  // --- Memoized Data Transformation for Credit Pack Table ---
  const creditPackTable = useMemo(() => {
    // Use local products if props empty
    const packs = creditPackProducts.length > 0 ? creditPackProducts : localProducts.creditPackProducts;
    // Find the specific ADD-ON product row for the $10 trial purchase to get its Stripe ID
    const trialAddonProduct = packs.find(p =>
      p.is_membership === false &&
      p.tier?.toLowerCase().includes('trial') &&
      p.image_credits === 25
    );
    const trialAddonPriceId = trialAddonProduct?.stripe_price_id;
    const trialAddonCostPerImage = trialAddonProduct ? (Number(trialAddonProduct.cost_per_image) || 0) : 0;

    return CREDIT_PACK_SIZES.map(size => {
      const row = {
        sizeLabel: `${size.toLocaleString()} Upload Credits`,
        sizeNumeric: size,
        prices: {}
      };

      // Populate TRIAL price info ONLY for the 25 credit row
      if (size === 25 && trialAddonPriceId && trialAddonCostPerImage > 0) {
        row.prices[TRIAL_TIER_KEY] = {
          priceId: trialAddonPriceId,
          costPerImage: trialAddonCostPerImage
        };
      } else if (size === 25) {
        row.prices[TRIAL_TIER_KEY] = null;
      }

      // Populate paid tier info
      for (const [tierKey, tierShortName] of Object.entries(PAID_PRICE_TIERS)) {
        const product = packs.find(p =>
          p.tier?.includes(tierShortName) && p.image_credits === size && p.is_membership === false
        );
        row.prices[tierKey] = product ? { priceId: product.stripe_price_id, costPerImage: Number(product.cost_per_image) || 0 } : null;
      }
      return row;
    });
  }, [subscriptionProducts, creditPackProducts, localProducts]);

  // --- Render Functions ---

  // Formats ISO date strings nicely
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const dt = DateTime.fromISO(isoString);
      return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY) : 'Invalid Date';
    } catch (e) {
      console.error("Error formatting date:", isoString, e);
      return 'Date Error';
    }
  };

  // Renders the status bar at the top
  const renderStatusBar = () => (
    <div className="text-center text-gray-700 bg-white p-4 rounded-lg shadow mb-8 space-y-2 border border-gray-200">
      <p className="text-lg">
        <strong>Membership Status:</strong>
        <span className={`ml-2 font-semibold ${safeMembership.status === 'cancelling' ? 'text-yellow-600' : isTrial ? 'text-blue-600' : 'text-green-600'}`}>
          {isTrial ? 'TRIAL MEMBERSHIP' : safeMembership.status?.toUpperCase() || 'N/A'}
        </span>
        {safeMembership.status === 'cancelling' && safeMembership.end_date && (
          <span className="text-sm text-gray-500"> (Ends {formatDate(safeMembership.end_date)})</span>
        )}
      </p>
      <p className="text-lg">
        <strong>Upload Credits Available:</strong> <span className="font-semibold">{safeMembership.total_images_avail ?? 0}</span>
      </p>
      {safeMembership.status === 'active' && safeMembership.end_date ? (
        <p className="text-lg">
          <strong>{isTrial ? 'Trial Ends On:' : 'Membership Renews On:'}</strong>
          <span className="font-semibold ml-1">{formatDate(safeMembership.end_date)}</span>
        </p>
      ) : null }
      <button
        type="button"
        onClick={() => {
          const el = document.getElementById('upload-credits-section');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
      >
        Purchase Upload Credits
      </button>
    </div>
  );

  // RETROFIT: Updated to call new handler (adds confirm + cancel only for active switches)
const renderSubscriptionTiers = () => (
  <div className="flex flex-wrap justify-center items-stretch -m-4">
    {subscriptionProducts
      .filter(p => !isTrialTier(p.tier))
      .map(product => {
        const isCurrentPlan = product.tier === safeMembership.tier;
        const price = typeof product.cost_per_image === 'number' ? product.cost_per_image : 0;
        const isDisabled = state.loading || (!isTrial && !isCancelling && isCurrentPlan); // Unchanged: disables current active
        const buttonText = isCurrentPlan ? 'Current Plan' : (isTrial || isCancelling ? 'Select Plan' : 'Change Plan');
        return (
          <div key={product.id} className={`flex-1 min-w-[250px] m-4 p-6 border rounded-lg shadow-lg bg-white flex flex-col ${isDisabled && !state.loading ? 'border-blue-600 border-2' : 'border-gray-300'}`}>
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-3">{product.tier}</h3>
            <p className="text-gray-600 mb-4 h-24 overflow-y-auto">{product.description || 'No description available.'}</p>
            <div className="mb-4 text-center">
              <span className="text-4xl font-bold">${price.toFixed(2)}</span>
              <span className="text-lg text-gray-500"> /mo</span>
            </div>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Billed monthly. Renews automatically until you cancel.
            </p>
            <button
              onClick={() => handleSelectSubscriptionTier(product)} // RETROFIT: New handler
              disabled={isDisabled}
              className="mt-auto w-full py-3 px-5 rounded-md text-white font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700"
            >
              {buttonText}
            </button>
          </div>
        );
      })}
  </div>
);

  // Renders the credit pack table (Styling adjustments)
  const renderCreditPackTable = () => (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-6">Purchase Additional Upload Credits</h2>
      <p className="text-sm text-gray-700 mb-2 text-center">
        Select a pack below to proceed to our secure checkout. Credits are added to your balance instantly and automatically roll over to the next month.
      </p>
      <div className="overflow-x-auto">
        <table className="w-fit mx-auto divide-y divide-gray-200 text-sm border border-gray-200 rounded-md overflow-hidden">
          <thead className="bg-gray-50">
            <tr className="divide-x divide-gray-200">
              <th className="px-3 py-2 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Credit Pack Size</th>
              {isTrial && (
                <th className="px-3 py-2 text-center text-[11px] font-bold text-gray-500 uppercase tracking-wider text-blue-600 whitespace-nowrap">Trial Offer Price</th>
              )}
              {Object.values(PAID_PRICE_TIERS).map(tierName => (
                <th key={tierName} className="px-3 py-2 text-center text-[11px] font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{tierName} Price</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {creditPackTable.map(row => (
              <tr key={row.sizeLabel} className="divide-x divide-gray-200">
                <td className="px-3 py-2 whitespace-nowrap font-semibold text-gray-900">{row.sizeLabel}</td>

                {isTrial && (
                  <td key={TRIAL_TIER_KEY} className="px-3 py-2 whitespace-nowrap text-center tabular-nums">
                    {row.sizeNumeric === 25 ? (
                      (() => {
                        const trialPriceInfo = row.prices[TRIAL_TIER_KEY];
                        const isValidTrialPurchase = trialPriceInfo && typeof trialPriceInfo.costPerImage === 'number' && trialPriceInfo.costPerImage > 0 && trialPriceInfo.priceId;
                        const trialTotalPrice = isValidTrialPurchase ? (trialPriceInfo.costPerImage * row.sizeNumeric).toFixed(2) : 'N/A';

                        return isValidTrialPurchase ? (
                          <button
                            onClick={() => handleStripePurchase(trialPriceInfo.priceId, 'payment')}
                            disabled={state.loading}
                            className="py-1.5 px-3 rounded-md font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
                          >
                            ${trialTotalPrice}
                          </button>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        );
                      })()
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                )}

                {Object.keys(PAID_PRICE_TIERS).map(tierKey => {
                  const priceInfo = row.prices[tierKey];
                  const isUserTier = safeMembership.tier === tierKey;
                  const isValidPrice = priceInfo && typeof priceInfo.costPerImage === 'number' && priceInfo.costPerImage >= 0;
                  const totalPrice = isValidPrice ? (priceInfo.costPerImage * row.sizeNumeric).toFixed(2) : 'N/A';
                  const showDash = tierKey === 'Business Membership' && row.sizeNumeric === 25;

                  return (
                    <td key={tierKey} className="px-3 py-2 whitespace-nowrap text-center tabular-nums">
                      {showDash ? (
                        <span className="text-gray-400 font-semibold">-</span>
                      ) : isValidPrice ? (
                        isTrial ? (
                          <span className="py-1.5 px-3 rounded-md font-semibold text-sm text-gray-500">${totalPrice}</span>
                        ) : (
                          <button
                            onClick={() => handleStripePurchase(priceInfo.priceId, 'payment')}
                            disabled={!isUserTier || state.loading}
                            className="py-1.5 px-3 rounded-md font-semibold text-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700 text-white"
                          >
                            ${totalPrice}
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // --- Main Component Render ---
  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8">
      <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-3">
        Membership Management
      </h1>

      {/* RENDER STATUS BAR */}
      {renderStatusBar()}
    {/* ADDED: Refresh spinner (covers stale during load) */}
{isRefreshing && (
  <div className="text-center py-6 bg-blue-50 border border-blue-200 rounded mb-6">
    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-2"></div>
    Updating your membership...
  </div>
)}
      {/* ADDED: Success toast */}
      {state.success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 text-center">
          <strong>Success!</strong> {state.success}
        </div>
      )}

      {/* Error/Success Feedback */}
      {state.error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert"><strong>Error:</strong> {state.error}</div>}

      {/* ADDED: Loading guard for local products */}
      {isLoadingLocalProducts && <div className="text-center py-4">Loading membership details...</div>}

      {/* Section 1: Subscription Tiers */}
      <div className="mb-12 rounded-xl bg-gradient-to-b from-slate-50 to-white p-6 border border-gray-200">
        {renderSubscriptionTiers()}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h2 className="text-base font-semibold text-center mb-2">
            Included with Every Plan
          </h2>
          <ul className="text-sm md:text-base text-gray-800 mb-3 flex flex-col md:flex-row md:flex-wrap justify-center gap-3 text-center">
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">Unlimited Inventory Capacity</span>
                <span className="block text-xs md:text-sm text-gray-600">Scale your sales without restrictions. Add thousands of items per estate without ever hitting a limit or paywall.</span>
              </p>
            </li>
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">Smart Listing Assistant</span>
                <span className="block text-xs md:text-sm text-gray-600">Stop typing from scratch. Instantly generate professional titles, descriptions, and intelligent price estimates for every item.</span>
              </p>
            </li>
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">Master Price List</span>
                <span className="block text-xs md:text-sm text-gray-600">Download your complete inventory and empower staff to look up the correct price for missing or removed tags.</span>
              </p>
            </li>
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">Remote Sale Prep</span>
                <span className="block text-xs md:text-sm text-gray-600">Upload client photos and start pricing your sale from the comfort of your home office. Get the hard work done before you ever set foot on the property.</span>
              </p>
            </li>
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">Universal Tag Printing</span>
                <span className="block text-xs md:text-sm text-gray-600">Print how you want. Format tags for standard home laser and inkjet printers (PDF) or mobile thermal label makers (Brother/Dymo/Zebra/Rollo).</span>
              </p>
            </li>
            <li className="flex-1 min-w-[220px] px-3 py-3 rounded-lg bg-gradient-to-b from-white to-slate-50 border border-gray-200 shadow-sm">
              <p>
                <span className="block font-semibold text-gray-900 mb-1">100% Credit Rollover</span>
                <span className="block text-xs md:text-sm text-gray-600">Don't lose what you paid for. Unused upload credits automatically roll over to the next month so you can bank them for your busy season.</span>
              </p>
            </li>
          </ul>
        </div>
      </div>

      {/* Section 2: Credit Packs */}
      <div id="upload-credits-section" className="mb-12">
        {renderCreditPackTable()}
      </div>

      {/* Section 3: Cancel / Reactivate Buttons */}
      {safeMembership.status === 'active' && !isTrial && (
        <div className="text-center">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">
            Cancel Membership
          </h3>
          <p className="text-sm text-gray-700 mb-3">
            Canceling stops future automatic renewal.
            Your plan remains active until your current renewal date.
            Unused credits remain on your cancelled account for 24 months.
          </p>
          <button onClick={handleCancelMembership} disabled={state.loading} className="py-2 px-6 rounded-md font-semibold text-red-600 bg-transparent border border-red-600 hover:bg-red-100 disabled:opacity-50">
            Cancel Membership
          </button>
        </div>
      )}
      {safeMembership.status === 'cancelling' && (
        <div className="text-center p-4 bg-yellow-100 border border-yellow-300 rounded-md">
          <p className="mb-2 font-medium">Your membership is scheduled to cancel.</p>
        </div>
      )}

      <p className="mt-8 text-sm text-gray-500 text-center">
        Summary only. For full legal details, see our{' '}
        <a
          href="/terms#membership"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Membership, Credits &amp; Refund Terms
        </a>
        .
      </p>
    </div>
  );
}

export default ActiveMemberView;
